import { GetAgregatedForecastResponse, GetAgregatedHistoryResponse, ListSchedulesResponseRules, ListSchedulesResponseRulesStateEnum, UpdateScheduleRequest, UpdateScheduleStateRequestNewStateEnum } from "@microtica/ms-cloud-cost-optimizer-sdk";
import { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import AwsLogo from "../../assets/aws-logo.svg";
import { getCostOptimizationService } from "../../backend";
import YearlyCostWidget from "../../components/dashboards/widgets/YearlyCostWidget";
import MetricsWidget from "../../components/dashboards/widgets/MetricsWidget";
import { Button, Card, Col, notification, Row, Space, Divider, Statistic, Dropdown } from "antd";
import ColumnWidget from "../../components/dashboards/widgets/ColumnWidget";
import Heading from "../../components/Heading";
import { LockOutlined } from "@ant-design/icons";
import { IEnvDetails, ISavingScheduleAggregatedUtilization, WidgetColor } from "../../types";
import { convertToCurrencyWithoutFractionDigits } from "../../utils/conversion";
import ExplanationButton from "../../components/explanations/ExplanationButton";
import CostDashboardExplorer from "../../components/explanations/CostDashboardExplorerExplanation";
import CostDashboardPotentialSavingsExplanation from "../../components/explanations/CostDashboardPotentialSavingsExplanation";
import CostDashboardSavingSchedulesExplanation from "../../components/explanations/CostDashboardSavingSchedulesExplanation";
import { TOTAL_HOURS_IN_MONTH, calculateUtilization, getLocalTimeZone } from "../../utils/cron";
import DisabledFeatureInfoCard from "../../components/dashboards/DisabledFeatureInfoCard";
import CreateSavingScheduleModal from "../../components/modals/CreateSavingScheduleModal";
import SavingScheduleWidget from "../../components/dashboards/widgets/SavingScheduleWidget";
import { useDoubleConfigurationModal } from "../../contexts/Modal";
import capitalizeFirstLetter from "../../utils/capitalize-first-letter";
import { PricingPlanNames } from "../../enums/enums";
import { currentProjectState } from "../../recoil/project";
import { useRecoilValue } from "recoil";
import { getExpectedMonthlySavingsAndCost } from "../../utils/environment/expected-monthly-savings-and-cost";
import MessageCard from "../../components/cards/MessageCard";

const CostDashboard = () => {
    const DEFAULT_COST_ALLOCATION_TAG = "microtica:environment";
    const LOCAL_TIME_ZONE = getLocalTimeZone();

    const { projectId } = useParams();
    const { envDetails } = useOutletContext<{ envDetails: IEnvDetails }>();
    const navigate = useNavigate();
    const { open: openConfirmModal } = useDoubleConfigurationModal();
    const currentProject = useRecoilValue(currentProjectState);
    const [isScheduleLoading, setIsScheduleLoading] = useState(true);
    const [isCostExplorerLoading, setIsCostExplorerLoading] = useState(true);
    const [isAWSCostExplorerEnabled, setIsAWSCostExplorerEnabled] = useState(true);

    // Saving schedule (advanced plan)
    const [isSavingScheduleEnabled, setIsSavingScheduleEnabled] = useState(false);
    const [isScheduleCreating, setIsScheduleCreating] = useState(false);
    const [schedule, setSchedule] = useState<ListSchedulesResponseRules>();
    const [savingScheduleAction, setSavingScheduleAction] = useState<"Create" | "Update">("Create");
    const [cronExpression, setCronExpression] = useState<{ startExpression: string; stopExpression: string; } | undefined>();
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [expectedMonthlySavingsAmount, setExpectedMonthlySavingsAmount] = useState(0);
    const [expectedMonthlyCostWithoutSchedule, setExpectedMonthlyCostWithoutSchedule] = useState(0);
    const [expectedMonthlyEc2AndRdsCost, setExpectedMonthlyEc2AndRdsCost] = useState(0);
    const [aggregatedUtilization, setAggregatedUtilization] = useState<ISavingScheduleAggregatedUtilization>();

    // Cost explorer (starter plan)
    const [isCostExplorerEnabled, setIsCostExplorerEnabled] = useState(false);
    const [estimatedMonthTrend, setEstimatedMonthTrend] = useState(0);
    const [estimatedYearTrend, setEstimatedYearTrend] = useState(0);
    const [totalYearCost, setTotalYearCost] = useState(0);
    const [montlyCostByService, setMontlyCostByService] = useState<{ value: number; type: string; entity: string; }[]>([]);
    const [yearCostByMonth, setYearCostByMonth] = useState<any[]>([]);
    const [aggregatedHistory, setAggregatedHistory] = useState<GetAgregatedHistoryResponse>();
    const [aggregatedForecast, setAggregatedForecast] = useState<GetAgregatedForecastResponse>();

    useEffect(() => {
        fetch({
            shouldUpdateCostExplorer: true,
            shouldUpdateSavingSchedule: true
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentProject]);

    const fetchSavingSchedule = async (expectedMonthlySavings: number) => {
        const { data: schedules } = await getCostOptimizationService().listSchedules(projectId!).catch(err => {
            if (err?.code === 402) {
                setIsSavingScheduleEnabled(false);
                setIsCostExplorerEnabled(false);
            }
            throw err;
        });
        const schedule = schedules?.rules?.find(r => r.resourcesTagKey === DEFAULT_COST_ALLOCATION_TAG && r.resourcesTagValue === envDetails.id);
        setSchedule(schedule);

        if (!!schedule) {
            const aggregatedUtilization = calculateUtilization(schedule, LOCAL_TIME_ZONE);

            setAggregatedUtilization(aggregatedUtilization);
            setSavingScheduleAction(aggregatedUtilization?.utilizedDays?.length ? "Update" : "Create");
            setCronExpression({
                startExpression: schedule.startScheduleExpression,
                stopExpression: schedule.stopScheduleExpression
            })
            const coeficient = (+(aggregatedUtilization.totalUtilizedHours / TOTAL_HOURS_IN_MONTH).toFixed(2));
            if (expectedMonthlySavings !== 0) {
                setExpectedMonthlySavingsAmount(expectedMonthlySavings * coeficient);
            }
        } else {
            setAggregatedUtilization(undefined);
            setSavingScheduleAction("Create");
            setCronExpression(undefined);
        }
    };

    const fetchCostExplorer = async ({
        estimatedEc2Cost,
        estimatedRdsCost,
        estimatedAllServicesCost
    }: {
        estimatedEc2Cost: number,
        estimatedRdsCost: number,
        estimatedAllServicesCost: number
    }) => {
        const [
            { data: costHistory },
            { data: costForecast },
        ] = await Promise.all([
            getCostOptimizationService().getAggregatedHistory(projectId!, DEFAULT_COST_ALLOCATION_TAG),
            getCostOptimizationService().getAggregatedForecast(projectId!, DEFAULT_COST_ALLOCATION_TAG).catch(e => {
                if (e.response?.data?.code === "DataUnavailableException") {
                    return { data: undefined };
                } else {
                    throw e;
                }
            })
        ]).catch(err => {
            if (err?.code === 402) {
                setIsCostExplorerEnabled(false);
            }
            throw err;
        });

        // Cost explorer
        if (costForecast && costHistory) {
            setEstimatedMonthTrend(Math.round(
                (costHistory.costForTheLastMonth.total - costForecast.estimatedCostForTheCurrentMonth.total) /
                costHistory.costForTheLastMonth.total * 100
            ));
            setEstimatedYearTrend(Math.round(
                (costHistory.costByAWSAccountForTheCurrentYear.cost[envDetails.awsAccountId!].total - costForecast.estimatedCostByAWSAccountForTheNextYear.cost[envDetails.awsAccountId!].total) /
                costHistory.costByAWSAccountForTheCurrentYear.cost[envDetails.awsAccountId!].total * 100
            ));

            const {
                ec2: currentEc2Cost,
                rds: currentRdsCost,
                allServices: currentAllServicesCost
            } = costHistory.costForTheCurrentMonth.ids[envDetails.awsAccountId!];

            setMontlyCostByService([
                { value: estimatedEc2Cost > currentEc2Cost ? estimatedEc2Cost - currentEc2Cost : 0, type: "Expected additional cost", entity: "EC2" },
                { value: currentEc2Cost, type: "Cost to date", entity: "EC2" },
                { value: estimatedRdsCost > currentRdsCost ? estimatedRdsCost - currentRdsCost : 0, type: "Expected additional cost", entity: "RDS" },
                { value: currentRdsCost, type: "Cost to date", entity: "RDS" },
                { value: estimatedAllServicesCost > currentAllServicesCost ? estimatedAllServicesCost - currentAllServicesCost : 0, type: "Expected additional cost", entity: "All Services" },
                { value: currentAllServicesCost, type: "Cost to date", entity: "All Services" }
            ]);

            const { months, total } = costHistory.costByAWSAccountForTheCurrentYear.cost[envDetails.awsAccountId!];
            setYearCostByMonth(months.map(m => ({ ...m, account: envDetails.awsAccountId! })));
            setTotalYearCost(Math.round(total));
        }
        setAggregatedHistory(costHistory);
        setAggregatedForecast(costForecast);
    };

    const fetch = async ({ shouldUpdateCostExplorer = true, shouldUpdateSavingSchedule = true }) => {
        try {
            if (shouldUpdateCostExplorer) {
                setIsCostExplorerLoading(true);
            }
            if (shouldUpdateSavingSchedule) {
                setIsScheduleLoading(true);
            }

            let estimatedEc2CostPerAwsAccount = 0;
            let estimatedRdsCostPerAwsAccount = 0;
            let expectedMonthlyEc2AndRdsCost = 0;
            let estimatedAllServicesCostPerAwsAccount = 0;

            const satisfiedCostExplorerRequirements = !!envDetails.awsAccountId;
            const satisfiedSavingScheduleRequirements = !!envDetails.awsAccountId && envDetails.totalDeployedResources > 0;
            // check if features are enabled
            const enabledCostExplorer = satisfiedCostExplorerRequirements && !!currentProject.paymentPlan?.limitations?.cloudCostOptimizer;
            const enabledSavingSchedule = satisfiedSavingScheduleRequirements && !!currentProject.paymentPlan?.limitations?.savingSchedules;
            setIsCostExplorerEnabled(enabledCostExplorer);
            setIsSavingScheduleEnabled(enabledSavingSchedule);

            if (!!envDetails.awsAccountId) {
                const { environment, awsAccount } = await getExpectedMonthlySavingsAndCost({
                    awsAccountId: envDetails.awsAccountId!,
                    awsRegion: envDetails.region!,
                    envId: envDetails.id,
                    projectId: projectId!
                })

                estimatedEc2CostPerAwsAccount = awsAccount.estimatedEc2Cost;
                estimatedRdsCostPerAwsAccount = awsAccount.estimatedRdsCost;
                expectedMonthlyEc2AndRdsCost = environment.estimatedEc2AndRdsCost;
                estimatedAllServicesCostPerAwsAccount = awsAccount.estimatedAllServicesCost;
                setExpectedMonthlySavingsAmount(environment.expectedMonthlySavings);
                setExpectedMonthlyCostWithoutSchedule(estimatedAllServicesCostPerAwsAccount);
                setExpectedMonthlyEc2AndRdsCost(expectedMonthlyEc2AndRdsCost);
            }
            if (enabledSavingSchedule && shouldUpdateSavingSchedule) {
                await fetchSavingSchedule(expectedMonthlyEc2AndRdsCost);
            }
            if (enabledCostExplorer && shouldUpdateCostExplorer) {
                await fetchCostExplorer({
                    estimatedEc2Cost: estimatedEc2CostPerAwsAccount,
                    estimatedRdsCost: estimatedRdsCostPerAwsAccount,
                    estimatedAllServicesCost: estimatedAllServicesCostPerAwsAccount
                })
            }

            setIsAWSCostExplorerEnabled(true);
        } catch (error) {
            if ((error as any).response?.status === 403) {
                setIsAWSCostExplorerEnabled(false);
            }

            notification.error({
                message: "Error getting cost metrics",
                description: (error as any).response?.data?.message
            })
        } finally {
            setIsCostExplorerLoading(false);
            setIsScheduleLoading(false);
        }
    };

    const updateScheduleState = async (newState: UpdateScheduleStateRequestNewStateEnum) => {
        const formattedStateName = capitalizeFirstLetter(newState);
        const explanation = newState === UpdateScheduleStateRequestNewStateEnum.DISABLED ? "you won't be saving money on your AWS bill anymore" : "you will start saving money on your AWS bill"
        // enable or disable schedule
        openConfirmModal({
            title: "Update schedule state",
            description:
                <Space direction='vertical'>
                    <Divider style={{ margin: "5px 0 15px 0" }} />
                    <div>This saving schedule will be <b>{formattedStateName}</b> and {explanation}.</div>
                </Space>,
            confirmation: schedule!.name,
            okText: formattedStateName.slice(0, -1),
            cancelText: "Cancel",
            onOk: async () => {
                await getCostOptimizationService().updateScheduleState(schedule!.id, projectId!, { newState });
                notification.success({
                    message: "Successfully updated schedule state"
                });
                fetch({
                    shouldUpdateCostExplorer: false,
                    shouldUpdateSavingSchedule: true
                });
            }
        });

    }

    const deleteSchedule = async () => {
        openConfirmModal({
            title: "Delete saving schedule",
            description:
                <Space direction='vertical'>
                    <Divider style={{ margin: "5px 0 15px 0" }} />
                    <div>This saving schedule will be deleted and you won't be saving money on your AWS bill anymore.</div>
                </Space>,
            confirmation: schedule!.name,
            okText: "Delete",
            cancelText: "Cancel",
            onOk: async () => {
                await getCostOptimizationService().deleteSchedule(schedule!.id, projectId!);
                notification.success({
                    message: "Successfully deleted schedule"
                });
                fetch({
                    shouldUpdateCostExplorer: false,
                    shouldUpdateSavingSchedule: true
                });
            }
        });
    }

    const handleScheduleAction = async () => {
        const createSchedule = async () => {
            setIsScheduleCreating(true);
            const scheduleOptions = {
                name: `${envDetails.name} Env Schedule`,
                startScheduleExpression: cronExpression!.startExpression, //"cron(0 6 ? * Monday,Tuesday,Wednesday,Thursday,Friday *)"
                stopScheduleExpression: cronExpression!.stopExpression, //"cron(0 16 ? * Monday,Tuesday,Wednesday,Thursday,Friday *)"
                awsAccountId: envDetails.awsAccountId!,
                region: envDetails.region!,
                tagKey: DEFAULT_COST_ALLOCATION_TAG,
                tagValue: envDetails.id,
                timezone: LOCAL_TIME_ZONE //"UTC+2"
            };
            await getCostOptimizationService().createSchedule(
                projectId!,
                scheduleOptions
            );
            notification.success({
                message: "Successfully created schedule"
            });
            setIsScheduleCreating(false);
        }

        const updateSchedule = async () => {
            const updateScheduleProps: UpdateScheduleRequest = {
                name: `${envDetails.name} Env Schedule`,
                startScheduleExpression: cronExpression!.startExpression,
                stopScheduleExpression: cronExpression!.stopExpression,
                timezone: LOCAL_TIME_ZONE
            }
            await getCostOptimizationService().updateSchedule(
                schedule!.id,
                projectId!,
                updateScheduleProps
            );
            notification.success({
                message: "Successfully updated schedule"
            });
        }

        if (savingScheduleAction === "Create") {
            await createSchedule();
        } else {
            await updateSchedule();
        }
        // reload schedule
        fetch({
            shouldUpdateCostExplorer: false,
            shouldUpdateSavingSchedule: true
        });
    }

    return (
        <Space direction="vertical" size={24} style={{ width: "inherit" }}>
            {expectedMonthlySavingsAmount === 0 ?
                null :
                <div className="saving-schedules">
                    <Heading
                        level={3}
                        title={
                            <Space direction="horizontal">
                                <Space style={!!currentProject.paymentPlan?.limitations?.savingSchedules ? {} : { color: "gray" }} direction="horizontal">
                                    {!currentProject.paymentPlan?.limitations?.savingSchedules && <LockOutlined />}
                                    Saving Schedules
                                </Space>
                                <ExplanationButton
                                    content={<CostDashboardSavingSchedulesExplanation />}
                                    blockElement={true}
                                />
                            </Space>
                        }
                    />
                    <div className="saving-schedules-content">
                        {
                            !isScheduleLoading && !isSavingScheduleEnabled ?
                                <DisabledFeatureInfoCard
                                    expectedSavings={expectedMonthlySavingsAmount}
                                    upgradePaymentPlanTo={PricingPlanNames.ADVANCED}
                                    envDetails={{
                                        id: envDetails.id,
                                        projectId: projectId!,
                                        missingAwsAccount: !envDetails.awsAccountId,
                                        noDeployedResources: envDetails.totalDeployedResources === 0
                                    }}
                                /> :
                                <div>
                                    <br />
                                    <Card
                                        title={
                                            !schedule ?
                                                null : (
                                                    <div style={{ position: "relative", display: "inline-block" }}>
                                                        {
                                                            schedule.state === ListSchedulesResponseRulesStateEnum.ENABLED &&
                                                                aggregatedUtilization?.utilizedDays.includes(new Date().toLocaleDateString("en-US", { weekday: "long" })) &&
                                                                (new Date().getHours() < aggregatedUtilization.startHourInTimeZone || new Date().getHours() > aggregatedUtilization.stopHourInTimeZone)
                                                                ?
                                                                <div className="pulsating-circle-green" title="Saving mode is ON"></div> :
                                                                <div className="gray-circle" title="Saving mode is OFF"></div>
                                                        }
                                                        <div style={{ marginLeft: 25 }}>
                                                            {schedule.name}
                                                        </div>
                                                    </div>
                                                )
                                        }
                                        loading={isScheduleLoading}
                                        extra={
                                            !schedule ?
                                                null :
                                                <Dropdown.Button
                                                    trigger={["click"]}
                                                    type="ghost"
                                                    onClick={() => setIsScheduleModalOpen(true)}
                                                    menu={{
                                                        items: [
                                                            {
                                                                key: "update-state",
                                                                label: `${schedule.state === ListSchedulesResponseRulesStateEnum.ENABLED ? "Disable" : "Enable"} schedule`,
                                                                onClick: () => updateScheduleState(schedule.state === ListSchedulesResponseRulesStateEnum.ENABLED ? UpdateScheduleStateRequestNewStateEnum.DISABLED : UpdateScheduleStateRequestNewStateEnum.ENABLED)
                                                            },
                                                            {
                                                                key: "delete",
                                                                label: "Delete schedule",
                                                                danger: true,
                                                                onClick: deleteSchedule
                                                            }
                                                        ]
                                                    }}
                                                >
                                                    Edit schedule
                                                </Dropdown.Button>
                                        }
                                    >
                                        {!!schedule ?
                                            <SavingScheduleWidget
                                                aggregatedUtilization={aggregatedUtilization}
                                                expectedMonthlySavingsAmount={expectedMonthlySavingsAmount}
                                                isLoading={isScheduleLoading}
                                                scheduleRule={schedule}
                                                timeZone={LOCAL_TIME_ZONE}
                                            /> :
                                            <Space className="flex-justify-space-between">
                                                <Card.Meta
                                                    title={
                                                        <Space direction="horizontal">
                                                            <div>Unlock the potential to save up to</div>
                                                            <Statistic
                                                                value={convertToCurrencyWithoutFractionDigits(expectedMonthlySavingsAmount)}
                                                                valueStyle={{ color: "green", lineHeight: "24px", fontSize: "20px" }}
                                                            />
                                                            <ExplanationButton
                                                                content={<CostDashboardPotentialSavingsExplanation />}
                                                                blockElement={true}
                                                            />
                                                        </Space>
                                                    }
                                                    description="Reduce cloud costs for non-production environments"
                                                />
                                                <Button
                                                    type="primary"
                                                    onClick={() => setIsScheduleModalOpen(true)}
                                                    loading={isScheduleCreating}
                                                >
                                                    Create schedule
                                                </Button>
                                            </Space>
                                        }
                                    </Card>
                                </div>
                        }
                    </div>
                </div>
            }
            <div className="cost-explorer">
                <Heading
                    level={3}
                    title={
                        <div className="flex-justify-space-between">
                            <Space direction="horizontal">
                                <Space style={!!currentProject.paymentPlan?.limitations?.cloudCostOptimizer ? {} : { color: "gray" }} direction="horizontal" size={8}>
                                    {!currentProject.paymentPlan?.limitations?.cloudCostOptimizer && <LockOutlined />}
                                    Cost Explorer
                                    {!envDetails.awsAccountId || isCostExplorerLoading || !isCostExplorerEnabled ?
                                        null :
                                        <Space size={4} style={{ color: "#252F3E", fontWeight: "normal", fontSize: "15px" }}>
                                            <img src={AwsLogo} alt="aws" style={{ width: "22px" }} />
                                            {envDetails.awsAccountId}
                                        </Space>
                                    }
                                </Space>
                                <ExplanationButton
                                    content={<CostDashboardExplorer />}
                                    blockElement={true}
                                />
                            </Space>
                            <Button onClick={() => navigate(`/projects/${projectId}/settings/cost-report`)}>
                                Configure Email Report
                            </Button>
                        </div>
                    }
                />

                <br />

                {
                    !isAWSCostExplorerEnabled &&
                    <>
                        <MessageCard
                            text={
                                <div>
                                    <b>Cost Explorer is not enabled</b> for your AWS account, or you do not currently have access. Please <a href="https://docs.aws.amazon.com/cost-management/latest/userguide/ce-enable.html" target="_blank">enable Cost Explorer</a> from the AWS Console and <a href="https://docs.aws.amazon.com/cost-management/latest/userguide/ce-access.html" target="_blank">verify your access</a> before attempting again.
                                </div>
                            }
                            type="error"
                        />
                        <br />
                    </>
                }


                <div className="cost-explorer-content">
                    {
                        !isCostExplorerLoading && !isCostExplorerEnabled ?
                            <DisabledFeatureInfoCard
                                upgradePaymentPlanTo={PricingPlanNames.STARTER}
                                envDetails={{
                                    id: envDetails.id,
                                    projectId: projectId!,
                                    missingAwsAccount: !envDetails.awsAccountId,
                                    // if there are no deployed resources in Free plan, cost explorer should have CTA for upgrading to Starter plan instead of a 'There are no deployed resources in this environment' info box
                                    noDeployedResources: false
                                }}
                            /> :
                            <div>
                                <Row gutter={24}>
                                    <Col span={10}>
                                        <Card title="Current month's cost" style={{ height: "361px" }}>
                                            <MetricsWidget
                                                title="Current month spent"
                                                loading={isCostExplorerLoading}
                                                value={aggregatedHistory?.costForTheCurrentMonth.ids[envDetails.awsAccountId!].allServices}
                                            />
                                            <Divider style={{ margin: isCostExplorerLoading ? "24px 0" : "32px 0" }} />
                                            <MetricsWidget
                                                title="Estimated cost for this month"
                                                loading={isCostExplorerLoading}
                                                value={aggregatedForecast?.estimatedCostForTheCurrentMonth.ids[envDetails.awsAccountId!].allServices}
                                                lastValue={aggregatedHistory?.costForTheLastMonth.ids[envDetails.awsAccountId!].allServices}
                                                trendThisMonth={estimatedMonthTrend}
                                            />
                                        </Card>
                                    </Col>
                                    <Col span={14}>
                                        <ColumnWidget
                                            title="Top AWS services by spending"
                                            description="Current month's AWS spending breakdown with estimated total"
                                            data={montlyCostByService}
                                            xField="entity"
                                            yField="value"
                                            seriesField="type"
                                            color={(datum) => datum.type === "Expected additional cost" ? WidgetColor.FadedBlue : WidgetColor.Blue}
                                            pattern={(_datum, color) => {
                                                if (color === WidgetColor.FadedBlue) {
                                                    return {
                                                        type: "line",
                                                        cfg: {
                                                            rotation: 135,
                                                            backgroundColor: WidgetColor.FadedBlue
                                                        }
                                                    }
                                                }
                                                return {
                                                    type: "dot",
                                                    cfg: {
                                                        rotation: 0,
                                                        fill: color
                                                    }
                                                }
                                            }}
                                            loading={isCostExplorerLoading}
                                        />
                                    </Col>
                                </Row>

                                <br />
                                <Row>
                                    <Col span={24}>
                                        <YearlyCostWidget
                                            data={yearCostByMonth}
                                            totalCostPastYear={totalYearCost}
                                            trendNextYear={estimatedYearTrend}
                                            totalCostNextYear={aggregatedForecast?.estimatedCostByAWSAccountForTheNextYear.cost[envDetails.awsAccountId!].total}
                                            loading={isCostExplorerLoading}
                                        />
                                    </Col>
                                </Row>
                            </div>
                    }
                </div>

                <CreateSavingScheduleModal
                    localTimeZone={LOCAL_TIME_ZONE}
                    savingScheduleAction={savingScheduleAction}
                    expectedMonthly={{
                        ec2AndRdsCost: expectedMonthlyEc2AndRdsCost,
                        costWithoutSchedule: expectedMonthlyCostWithoutSchedule
                    }}
                    isModalOpen={isScheduleModalOpen}
                    setIsModalOpen={setIsScheduleModalOpen}
                    setCronExpression={setCronExpression}
                    handleScheduleAction={handleScheduleAction}
                    aggregatedUtilization={aggregatedUtilization}
                />
            </div>
        </Space>
    )
}

export default CostDashboard;